import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, ImageIcon, Loader2, X, Download } from 'lucide-react';
import api, { assetUrl } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const PROVIDERS = [
  { id: 'nano-banana', label: 'Gemini Nano Banana', vendor: 'Google' },
  { id: 'gpt-image-1', label: 'GPT Image 1', vendor: 'OpenAI' },
];
const ASPECTS = [
  { id: 'youtube', label: '16:9 · YouTube thumbnail' },
  { id: 'square', label: '1:1 · Square' },
];
const IDEAS = [
  'Bold MrBeast-style thumbnail: shocked face, glowing money stacks, high contrast',
  'Cinematic tech thumbnail: a glowing AI chip floating over a dark city',
  'Cozy cooking thumbnail: steaming bowl of ramen, warm bokeh background',
  'Epic history thumbnail: ancient ruins at golden hour, dramatic sky',
];

export default function ImageStudio() {
  const [prompt, setPrompt] = useState('');
  const [provider, setProvider] = useState('nano-banana');
  const [aspect, setAspect] = useState('youtube');
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);

  const load = async () => {
    try { const { data } = await api.get('/images?kind=thumbnail'); setItems(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (prompt.trim().length < 3) { toast.error('Describe the thumbnail you want'); return; }
    setBusy(true);
    try {
      await api.post('/images/generate', { prompt: prompt.trim(), provider, aspect, kind: 'thumbnail', style: 'bold, high-contrast, eye-catching' });
      toast.success('Thumbnail generated');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Generation failed'); }
    finally { setBusy(false); }
  };

  const remove = async (aid) => { await api.delete(`/images/${aid}`); load(); };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <span className="grid place-items-center w-8 h-8 rounded-lg bg-[#FF4D00]/15"><Sparkles className="w-4 h-4 text-[#FF884D]" /></span>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Thumbnail & Image Studio</h1>
      </div>
      <p className="text-slate-400 mb-8">Generate scroll-stopping thumbnails and images from a text prompt — powered by Nano Banana or GPT Image 1.</p>

      <div className="rounded-2xl border border-white/10 bg-[#12141D] p-6">
        <div className="text-sm text-slate-400 mb-1.5">Describe your image</div>
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="thumb-prompt"
          placeholder="e.g. A bold YouTube thumbnail: a surprised creator pointing at a giant glowing arrow, vibrant colors"
          className="bg-[#0B0C12] border-white/12 min-h-[96px]" />

        <div className="flex flex-wrap gap-2 mt-3">
          {IDEAS.map((i) => (
            <button key={i} onClick={() => setPrompt(i)} data-testid="thumb-idea"
              className="text-xs text-slate-400 border border-white/10 rounded-full px-3 py-1.5 hover:border-white/30 hover:text-white transition-colors">
              {i.slice(0, 42)}…
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div>
            <div className="text-sm text-slate-400 mb-1.5">Image model</div>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger className="bg-[#0B0C12] border-white/12" data-testid="thumb-provider"><SelectValue /></SelectTrigger>
              <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`thumb-provider-${p.id}`}>{p.label} <span className="text-slate-500">· {p.vendor}</span></SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1.5">Aspect ratio</div>
            <Select value={aspect} onValueChange={setAspect}>
              <SelectTrigger className="bg-[#0B0C12] border-white/12" data-testid="thumb-aspect"><SelectValue /></SelectTrigger>
              <SelectContent>{ASPECTS.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`thumb-aspect-${a.id}`}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <Button data-testid="generate-thumbnail-btn" onClick={generate} disabled={busy} className="rounded-full mt-5 h-11 px-6 font-semibold">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><ImageIcon className="w-4 h-4 mr-2" /> Generate</>}
        </Button>
      </div>

      <div className="mt-8">
        <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">Your generations</div>
        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/12 bg-[#0B0C12] p-12 text-center text-slate-500" data-testid="thumb-empty">
            Nothing yet — generate your first thumbnail above.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((a) => (
              <div key={a.id} className="rounded-2xl overflow-hidden border border-white/10 bg-[#12141D] group" data-testid={`thumb-item-${a.id}`}>
                <div className="relative">
                  <img src={assetUrl(a.id, a.created_at)} alt="" className={`w-full object-cover ${a.aspect === 'square' ? 'aspect-square' : 'aspect-video'}`} />
                  <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <a href={assetUrl(a.id, a.created_at, true)} data-testid={`thumb-download-${a.id}`} className="grid place-items-center w-8 h-8 rounded-lg bg-black/60 hover:bg-[#FF4D00]"><Download className="w-4 h-4" /></a>
                    <button onClick={() => remove(a.id)} data-testid={`thumb-delete-${a.id}`} className="grid place-items-center w-8 h-8 rounded-lg bg-black/60 hover:bg-rose-600"><X className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="px-3 py-2.5">
                  <div className="text-xs text-slate-400 line-clamp-2">{a.prompt}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wide text-slate-600 mt-1.5">{a.provider}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
